import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, Zap, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BatchGenerationPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [selectedIdeas, setSelectedIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchData();
  }, [doctorId]);

  const fetchData = async () => {
    try {
      const [doctorRes, ideasRes] = await Promise.all([
        axios.get(`${API}/doctors/${doctorId}`),
        axios.get(`${API}/ideas/${doctorId}`)
      ]);
      setDoctor(doctorRes.data);
      setIdeas(ideasRes.data.ideas || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleIdea = (ideaId) => {
    setSelectedIdeas(prev =>
      prev.includes(ideaId)
        ? prev.filter(id => id !== ideaId)
        : [...prev, ideaId]
    );
  };

  const selectAll = () => {
    if (selectedIdeas.length === ideas.length) {
      setSelectedIdeas([]);
    } else {
      setSelectedIdeas(ideas.map(idea => idea.id));
    }
  };

  const handleBatchGenerate = async () => {
    if (selectedIdeas.length === 0) {
      toast.error('Please select at least one idea');
      return;
    }

    if (selectedIdeas.length > 5) {
      toast.error('Please select maximum 5 ideas at a time');
      return;
    }

    setGenerating(true);
    setProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 1000);

      const response = await axios.post(`${API}/content/batch-scripts`, {
        doctor_id: doctorId,
        idea_ids: selectedIdeas,
        language: doctor?.language_preference || 'English',
        country: doctor?.country || 'India'
      });

      clearInterval(progressInterval);
      setProgress(100);

      toast.success(`Generated ${response.data.count} scripts successfully!`);
      
      setTimeout(() => {
        navigate(`/library/${doctorId}`);
      }, 1500);
    } catch (error) {
      console.error('Error generating scripts:', error);
      toast.error('Failed to generate scripts');
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/ideas/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="batch-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Batch Script Generation
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#5C7063]">
              {selectedIdeas.length} selected
            </span>
            <Button
              onClick={handleBatchGenerate}
              disabled={generating || selectedIdeas.length === 0}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
              data-testid="batch-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  Generating... {progress}%
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Generate Scripts
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Generate Multiple Scripts
          </h1>
          <p className="text-lg text-[#5C7063] mb-4">
            Select up to 5 ideas to generate scripts in one go
          </p>
          <Button
            onClick={selectAll}
            variant="outline"
            size="sm"
            className="rounded-full border-[#2C5F44]/20"
            data-testid="batch-select-all-btn"
          >
            {selectedIdeas.length === ideas.length ? 'Deselect All' : 'Select All'}
          </Button>
        </motion.div>

        {ideas.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-[#82A08B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-12 h-12 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl font-bold text-[#1C2A21] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No Ideas Available
            </h2>
            <p className="text-lg text-[#5C7063] mb-8">
              Generate content ideas first to use batch generation
            </p>
            <Button
              onClick={() => navigate(`/ideas/${doctorId}`)}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
            >
              Generate Ideas
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {ideas.map((idea, index) => (
              <motion.div
                key={idea.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                onClick={() => toggleIdea(idea.id)}
                className={`bg-white rounded-3xl p-8 cursor-pointer border-2 transition-all ${
                  selectedIdeas.includes(idea.id)
                    ? 'border-[#2C5F44] shadow-[0_8px_30px_rgba(44,95,68,0.12)]'
                    : 'border-[#2C5F44]/10 hover:border-[#2C5F44]/30'
                }`}
                data-testid={`batch-idea-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <span className="inline-block px-4 py-1 bg-[#82A08B]/20 text-[#2C5F44] rounded-full text-sm font-medium mb-3">
                      {idea.format}
                    </span>
                    <h3 className="text-xl font-bold text-[#1C2A21] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {idea.topic}
                    </h3>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedIdeas.includes(idea.id)
                      ? 'bg-[#2C5F44] border-[#2C5F44]'
                      : 'border-[#2C5F44]/20'
                  }`}>
                    {selectedIdeas.includes(idea.id) && (
                      <CheckCircle className="w-4 h-4 text-white" strokeWidth={2} />
                    )}
                  </div>
                </div>
                <p className="text-sm text-[#5C7063] mb-3">
                  <strong>Hook:</strong> {idea.hook}
                </p>
                <p className="text-sm text-[#5C7063] italic">
                  ✨ {idea.why_it_works}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Box */}
        {ideas.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-8 text-white"
          >
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Batch Generation Tips
            </h2>
            <ul className="space-y-2 text-white/90">
              <li>• Select up to 5 ideas at once for optimal generation speed</li>
              <li>• Each script will be compliance-checked based on your country</li>
              <li>• Scripts will be saved to your Content Library automatically</li>
              <li>• Generation takes approximately 10-15 seconds per script</li>
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
}
